/*
 * BlackMirror Demo Attribution v1
 *
 * Captures first/last marketing touch on a Speediance Shopify storefront and
 * carries it into Cal.com using Cal.com's native UTM Tracking record. The full
 * payload is compacted into utm_content as `bm1.<base64url-json>`; it contains
 * marketing identifiers only and deliberately excludes names, email, phone,
 * arbitrary query parameters, and URL fragments.
 */
(function universalModule(root, factory) {
  "use strict";
  var api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root) root.BMDemoAttribution = api;
  if (root && root.document) api.install(root);
})(typeof window !== "undefined" ? window : null, function factory() {
  "use strict";

  var VERSION = 1;
  var COOKIE_NAME = "bm_demo_attribution_v1";
  var STORAGE_KEY = "bm_demo_attribution_v1";
  var COOKIE_MAX_AGE = 90 * 24 * 60 * 60;
  var CAL_HOST = "cal.futuremvmt.com";
  var PACKED_PREFIX = "bm1.";
  var CLICK_KEYS = ["gclid", "gbraid", "wbraid"];
  var UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
  var EXTRA_KEYS = ["tmsrc", "tmcid", "tmsid", "tmid"];
  var ALL_MARKETING_KEYS = CLICK_KEYS.concat(UTM_KEYS, EXTRA_KEYS);

  function clean(value, maxLength) {
    var text = value == null ? "" : String(value).trim();
    if (!text) return "";
    return text.slice(0, maxLength || 256);
  }

  function canonicalUrl(value, base) {
    try {
      var parsed = new URL(value, base || undefined);
      if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return "";
      return clean(parsed.origin + parsed.pathname, 700);
    } catch (_error) {
      return "";
    }
  }

  function marketingParams(search) {
    var params;
    try {
      params = search instanceof URLSearchParams ? search : new URLSearchParams(search || "");
    } catch (_error) {
      params = new URLSearchParams();
    }
    var output = {};
    ALL_MARKETING_KEYS.forEach(function (key) {
      var value = clean(params.get(key), key.indexOf("utm_") === 0 ? 300 : 256);
      if (value) output[key] = value;
    });
    return output;
  }

  function hasMarketingSignal(params) {
    return ALL_MARKETING_KEYS.some(function (key) { return Boolean(params && params[key]); });
  }

  function isExternalReferrer(referrer, currentOrigin) {
    if (!referrer) return false;
    try {
      return new URL(referrer).origin !== currentOrigin;
    } catch (_error) {
      return false;
    }
  }

  function buildTouch(locationLike, referrer, capturedAt) {
    var origin = clean(locationLike && locationLike.origin, 300);
    var params = marketingParams(locationLike && locationLike.search);
    var touch = {
      captured_at: capturedAt || new Date().toISOString(),
      landing_page: canonicalUrl(locationLike && locationLike.href, origin),
      referrer: isExternalReferrer(referrer, origin) ? canonicalUrl(referrer) : "",
    };
    ALL_MARKETING_KEYS.forEach(function (key) {
      if (params[key]) touch[key] = params[key];
    });
    return touch;
  }

  function normalizeTouch(input) {
    var source = input && typeof input === "object" ? input : {};
    var touch = {};
    var capturedAt = clean(source.captured_at, 40);
    var landingPage = canonicalUrl(source.landing_page);
    var referrer = canonicalUrl(source.referrer);
    if (capturedAt) touch.captured_at = capturedAt;
    if (landingPage) touch.landing_page = landingPage;
    if (referrer) touch.referrer = referrer;
    ALL_MARKETING_KEYS.forEach(function (key) {
      var value = clean(source[key], key.indexOf("utm_") === 0 ? 300 : 256);
      if (value) touch[key] = value;
    });
    return touch;
  }

  function normalizeState(input) {
    if (!input || typeof input !== "object") return null;
    var first = normalizeTouch(input.first_touch);
    var last = normalizeTouch(input.last_touch || input.first_touch);
    if (!first.landing_page && !last.landing_page) return null;
    return {
      schema_version: VERSION,
      store_host: clean(input.store_host, 255),
      first_touch: first,
      last_touch: last,
    };
  }

  function updateState(previous, currentTouch, storeHost) {
    var existing = normalizeState(previous);
    var current = normalizeTouch(currentTouch);
    if (!existing) {
      return normalizeState({
        schema_version: VERSION,
        store_host: storeHost,
        first_touch: current,
        last_touch: current,
      });
    }

    var hasNewSignal = hasMarketingSignal(current);
    var hasNewReferrer = Boolean(current.referrer);
    return normalizeState({
      schema_version: VERSION,
      store_host: clean(storeHost, 255) || existing.store_host,
      first_touch: existing.first_touch,
      last_touch: hasNewSignal || hasNewReferrer ? current : existing.last_touch,
    });
  }

  function compactTouch(touch) {
    var map = {
      captured_at: "ts", landing_page: "lp", referrer: "rf",
      gclid: "g", gbraid: "gb", wbraid: "wb",
      utm_source: "us", utm_medium: "um", utm_campaign: "uc",
      utm_term: "ut", utm_content: "uo",
      tmsrc: "ms", tmcid: "mc", tmsid: "md", tmid: "mi",
    };
    var output = {};
    Object.keys(map).forEach(function (key) {
      if (touch && touch[key]) output[map[key]] = touch[key];
    });
    return output;
  }

  function expandTouch(touch) {
    var map = {
      ts: "captured_at", lp: "landing_page", rf: "referrer",
      g: "gclid", gb: "gbraid", wb: "wbraid",
      us: "utm_source", um: "utm_medium", uc: "utm_campaign",
      ut: "utm_term", uo: "utm_content",
      ms: "tmsrc", mc: "tmcid", md: "tmsid", mi: "tmid",
    };
    var output = {};
    Object.keys(map).forEach(function (key) {
      if (touch && touch[key]) output[map[key]] = touch[key];
    });
    return normalizeTouch(output);
  }

  function utf8ToBase64Url(value) {
    var bytes = unescape(encodeURIComponent(value));
    var encoded;
    if (typeof btoa === "function") {
      encoded = btoa(bytes);
    } else if (typeof Buffer !== "undefined") {
      encoded = Buffer.from(value, "utf8").toString("base64");
    } else {
      throw new Error("No base64 encoder available");
    }
    return encoded.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
  }

  function base64UrlToUtf8(value) {
    var padded = String(value || "").replace(/-/g, "+").replace(/_/g, "/");
    while (padded.length % 4) padded += "=";
    if (typeof atob === "function") return decodeURIComponent(escape(atob(padded)));
    if (typeof Buffer !== "undefined") return Buffer.from(padded, "base64").toString("utf8");
    throw new Error("No base64 decoder available");
  }

  function packState(state) {
    var normalized = normalizeState(state);
    if (!normalized) return "";
    return PACKED_PREFIX + utf8ToBase64Url(JSON.stringify({
      v: VERSION,
      sh: normalized.store_host,
      f: compactTouch(normalized.first_touch),
      l: compactTouch(normalized.last_touch),
    }));
  }

  function unpackState(value) {
    var packed = clean(value, 7000);
    if (packed.indexOf(PACKED_PREFIX) !== 0) return null;
    try {
      var decoded = JSON.parse(base64UrlToUtf8(packed.slice(PACKED_PREFIX.length)));
      if (decoded.v !== VERSION) return null;
      return normalizeState({
        schema_version: decoded.v,
        store_host: decoded.sh,
        first_touch: expandTouch(decoded.f),
        last_touch: expandTouch(decoded.l),
      });
    } catch (_error) {
      return null;
    }
  }

  function augmentCalUrl(value, state, base) {
    var parsed;
    try { parsed = new URL(value, base || undefined); } catch (_error) { return value; }
    if (parsed.hostname.toLowerCase() !== CAL_HOST) return value;
    var normalized = normalizeState(state);
    if (!normalized) return parsed.toString();

    var last = normalized.last_touch || {};
    var hasGoogleClick = Boolean(last.gclid || last.gbraid || last.wbraid);
    var source = last.utm_source || (hasGoogleClick ? "google" : last.tmsrc || "");
    var medium = last.utm_medium || (hasGoogleClick ? "cpc" : "");
    var standard = {
      utm_source: source,
      utm_medium: medium,
      utm_campaign: last.utm_campaign || "",
      utm_term: last.utm_term || "",
    };
    Object.keys(standard).forEach(function (key) {
      if (standard[key]) parsed.searchParams.set(key, standard[key]);
    });
    parsed.searchParams.set("utm_content", packState(normalized));
    return parsed.toString();
  }

  function readCookie(documentLike) {
    var cookies = String((documentLike && documentLike.cookie) || "").split(/;\s*/);
    for (var index = 0; index < cookies.length; index += 1) {
      var separator = cookies[index].indexOf("=");
      var name = separator >= 0 ? cookies[index].slice(0, separator) : cookies[index];
      if (name !== COOKIE_NAME) continue;
      try { return unpackState(decodeURIComponent(cookies[index].slice(separator + 1))); } catch (_error) { return null; }
    }
    return null;
  }

  function readStoredState(windowLike) {
    var fromCookie = readCookie(windowLike.document);
    if (fromCookie) return fromCookie;
    try { return unpackState(windowLike.localStorage.getItem(STORAGE_KEY)); } catch (_error) { return null; }
  }

  function analyticsStorageAllowed(windowLike) {
    try {
      var privacy = windowLike.Shopify && windowLike.Shopify.customerPrivacy;
      // Fail closed: never persist or hand attribution to Cal until Shopify's
      // consent API explicitly says analytics processing is allowed.
      if (!privacy || typeof privacy.analyticsProcessingAllowed !== "function") return false;
      return privacy.analyticsProcessingAllowed() === true;
    } catch (_error) {
      return false;
    }
  }

  function persistState(windowLike, state) {
    if (!analyticsStorageAllowed(windowLike)) return false;
    var packed = packState(state);
    if (!packed) return false;
    try { windowLike.localStorage.setItem(STORAGE_KEY, packed); } catch (_error) {}
    try {
      windowLike.document.cookie = COOKIE_NAME + "=" + encodeURIComponent(packed)
        + ";path=/;max-age=" + COOKIE_MAX_AGE + ";SameSite=Lax;Secure";
    } catch (_error) {}
    return true;
  }

  function findFormDestination(form) {
    if (!form || typeof form.closest !== "function") return "";
    var host = form.closest("book-a-demo-v2, book-a-demo, [data-section-id]");
    if (!host) return "";
    var dataScript = host.querySelector('script[type="application/json"][id^="locations-"]');
    var demoType = host.querySelector('select[id^="demoType-"]');
    var country = host.querySelector('select[id^="country-"]');
    if (!dataScript || !demoType || !country || !demoType.value || !country.value) return "";
    var locations;
    try { locations = (JSON.parse(dataScript.textContent || "{}").locations || []); } catch (_error) { return ""; }
    var handle = demoType.value === "physical"
      ? ((host.querySelector('input[name="location"]:checked') || {}).value || "")
      : country.value;
    var location = locations.filter(function (item) { return item.country_handle === handle; })[0];
    if (!location) return "";
    var keyByType = {
      physical: "url_physical",
      virtual: "url_virtual",
      quick_qa: "url_quick_qa",
      post_sale: "url_post_sale",
    };
    return clean(location[keyByType[demoType.value]], 2000);
  }

  function install(windowLike) {
    if (windowLike.__bmDemoAttributionInstalled) return;
    windowLike.__bmDemoAttributionInstalled = true;
    var currentState = null;
    var consentGranted = false;

    function enableAfterConsent() {
      if (!analyticsStorageAllowed(windowLike)) return false;
      consentGranted = true;
      currentState = updateState(
        readStoredState(windowLike),
        buildTouch(windowLike.location, windowLike.document.referrer, new Date().toISOString()),
        windowLike.location.hostname
      );
      persistState(windowLike, currentState);
      return true;
    }

    enableAfterConsent();

    windowLike.document.addEventListener("visitorConsentCollected", function () {
      enableAfterConsent();
    });

    windowLike.document.addEventListener("click", function (event) {
      if (!consentGranted || !currentState) return;
      var target = event.target && typeof event.target.closest === "function"
        ? event.target.closest("a[href]")
        : null;
      if (!target) return;
      var augmented = augmentCalUrl(target.href, currentState, windowLike.location.href);
      if (augmented !== target.href) target.href = augmented;
    }, true);

    windowLike.document.addEventListener("submit", function (event) {
      if (!consentGranted || !currentState) return;
      var destination = findFormDestination(event.target);
      if (!destination) return;
      var augmented = augmentCalUrl(destination, currentState, windowLike.location.href);
      if (augmented === destination) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      windowLike.location.assign(augmented);
    }, true);
  }

  return {
    VERSION: VERSION,
    COOKIE_NAME: COOKIE_NAME,
    STORAGE_KEY: STORAGE_KEY,
    PACKED_PREFIX: PACKED_PREFIX,
    marketingParams: marketingParams,
    buildTouch: buildTouch,
    updateState: updateState,
    normalizeState: normalizeState,
    packState: packState,
    unpackState: unpackState,
    augmentCalUrl: augmentCalUrl,
    findFormDestination: findFormDestination,
    analyticsStorageAllowed: analyticsStorageAllowed,
    install: install,
  };
});
